import { Button, Callout, Spinner, TextArea } from '@blueprintjs/core'
import { DeviceDetailsBlock, useDeviceDetailsContext } from '../..'
import API, { RFCommand, RFPacket } from '../../../../../../API'
import Time from '../../../../../../Components/Time'
import DeviceCommandViewer from './Components/DeviceCommandViewer'
import React, { useState, useEffect } from 'react'
import DeviceResetButton from './Components/DeviceResetButton'
import Lang from '../../../../../../Lang'

export default function AdminTabPanel() {
	const { device, view, isLoadingAdditionalDetails, additionalDetails } = useDeviceDetailsContext()

	return (
		<>
			<DeviceDetailsBlock title="Details">
				Received <strong>{device.reportHealth}</strong> reports in last 24h
			</DeviceDetailsBlock>
			<DeviceDetailsGSMBlock device={device} />
			<DeviceDetailsGatewayBlock device={device} />
			<DeviceDetailsBlock title="Last Received Commands">
				{isLoadingAdditionalDetails ? (
					<Spinner size={20} />
				) : additionalDetails?.lastCommands !== null ? (
					<DeviceCommandViewer commands={additionalDetails.lastCommands} device={device} />
				) : (
					<div>No last commands found</div>
				)}
			</DeviceDetailsBlock>
			<DeviceDetailsConfigurationBlock device={device} />
			<DeviceDetailsStreetConfigurationBlock device={device} />
			<DeviceDetailsBlock title={Lang.get('Danger zone')} isDanger>
				<DeviceResetButton device={device} selectDevice={view?.selectDevice} />
			</DeviceDetailsBlock>
		</>
	)
}

function DeviceDetailsGSMBlock({ device }) {
	const [gateway, setGateway] = useState(null)

	useEffect(() => {
		let isMounted = true
		const fetchGatewayData = async () => {
			const list = await API.call('Admin.CommandBridge.Agent.List')
			console.log('list', list)
			let foundGateway = null
			for (let con of list) {
				if (con.scanReport && con.scanReport.deviceId == device.id) {
					foundGateway = con
					break
				}
			}
			if (isMounted) {
				setGateway(foundGateway)
			}
		}

		fetchGatewayData()

		return () => {
			isMounted = false
		}
	}, [device.id]) // re-run if device.id changes

	if (!gateway) {
		return null
	}

	return (
		<DeviceDetailsBlock title="GSM Connection">
			<table>
				<tbody>
					<tr>
						<td>Connected</td>
						<td className="value">
							<Time iso={gateway.timeConnected} />
						</td>
					</tr>
					<tr>
						<td>Address</td>
						<td className="value">{gateway.address}</td>
					</tr>
					<tr>
						<td>Firmware</td>
						<td className="value">{gateway.scanReport.firmwareVersion}</td>
					</tr>
				</tbody>
			</table>
		</DeviceDetailsBlock>
	)
}

// =============================================================================

function DeviceDetailsGatewayBlock({ device }) {
	const sortGateway = (a, b) => {
		if (a.lastDataTime > b.lastDataTime) {
			return -1
		} else if (a.lastDataTime < b.lastDataTime) {
			return 1
		}
		if (a.rssi > b.rssi) {
			return -1
		} else if (a.rssi < b.rssi) {
			return 1
		}
		return 0
	}

	return (
		<DeviceDetailsBlock title="Gateways">
			<table>
				<tbody>
					{device.gateways?.sort(sortGateway).map((g, idx) => (
						<tr key={`ddgateway-${idx}${g.deviceId}`}>
							<td>{g.deviceId}</td>
							<td>
								<Time timestamp={g.lastDataTime} />
							</td>
							<td>{g.rssi}</td>
						</tr>
					))}
				</tbody>
			</table>
		</DeviceDetailsBlock>
	)
}

// =============================================================================

function DeviceDetailsConfigurationBlock({ device }) {
	const [busy, setBusy] = useState(false)
	const [value, setValue] = useState('')
	const [error, setError] = useState(null)

	const performWithPacket = async (p) => {
		if (busy) return
		setBusy(true)
		setError(null)

		const r = await API.sendPacket({
			deviceId: device.id,
			command: p.command,
			payloadHex: p.toRaw().toString('hex'),
			responseCommand: RFCommand.CONFIG_REPORT
		})

		if (r) {
			setValue(JSON.stringify(r.config, null, 2))
			setBusy(false)
		} else {
			setValue('')
			setBusy(false)
			setError('Operation Failed!')
		}
		return r
	}

	const readConfig = async () => {
		const p = new RFPacket()
		p.command = RFCommand.CONFIG_READ
		await performWithPacket(p)
	}

	const writeConfig = async () => {
		try {
			const config = JSON.parse(value)

			const p = new RFPacket()
			p.command = RFCommand.CONFIG_WRITE
			p.data = Buffer.alloc(11)
			p.data.writeUInt8(config.operationMode, 0)
			p.data.writeUInt8(config.daliSpeed, 1)
			p.data.writeUInt8(config.idleBrightness, 2)
			p.data.writeUInt16LE(config.activeTime, 3)
			p.data.writeUInt8(config.activeBrightness, 5)
			p.data.writeUInt8(config.activeBrightnessN1, 6)
			p.data.writeUInt8(config.activeBrightnessN2, 7)
			p.data.writeUInt8(config.activeBrightnessN3, 8)
			p.data.writeUInt8(config.activeBrightnessN4, 9)
			p.data.writeUInt8(config.activeBrightnessN5, 10)
			await performWithPacket(p)
		} catch (e) {
			setError('Invalid JSON format for configuration.')
			setBusy(false)
		}
	}

	return (
		<DeviceDetailsBlock title="Configuration">
			<TextArea style={{ height: '210px' }} disabled={busy} value={value} onChange={(event) => setValue(event.target.value)} />
			{error && (
				<Callout icon="error" intent="danger">
					{error}
				</Callout>
			)}
			<div className="buttons">
				<Button fill text="Read" intent="success" disabled={busy} onClick={readConfig} />
				<Button fill text="Write" intent="danger" disabled={busy} onClick={writeConfig} />
			</div>
		</DeviceDetailsBlock>
	)
}

// =============================================================================

function DeviceDetailsStreetConfigurationBlock({ device }) {
	const [busy, setBusy] = useState(false)
	const [value, setValue] = useState('')
	const [error, setError] = useState(null)

	const readStreetConfig = async () => {
		if (busy) return
		setBusy(true)
		setError(null)

		const p = new RFPacket()
		p.command = RFCommand.SCAN
		const r = await API.sendPacket({
			deviceId: device.id,
			command: p.command,
			payloadHex: p.toRaw().toString('hex'),
			responseCommand: RFCommand.SCAN_REPORT
		})
		if (r) {
			let out = {
				operationMode: r.scanReport.operationMode,
				streetId: r.scanReport.streetId,
				poleId: r.scanReport.poleId
			}
			setValue(JSON.stringify(out, null, 2))
			setBusy(false)
		} else {
			setValue('')
			setBusy(false)
			setError('Operation Failed!')
		}
	}

	const writeStreetConfig = async () => {
		if (busy) return
		setBusy(true)
		setError(null)

		try {
			const config = JSON.parse(value)

			const p = new RFPacket()
			p.command = RFCommand.SET_IDENTITY
			p.destinationId = 0 // broadcast
			let buf = Buffer.alloc(13)
			buf.writeUInt32LE(device.id, 0)
			buf.writeUInt32LE(config.streetId, 4)
			buf.writeUInt32LE(config.poleId, 8)
			buf.writeUInt8(config.operationMode, 12)
			const serialBuf = Buffer.from(device.serialNr, 'hex')
			buf = Buffer.concat([serialBuf, buf])
			p.data = buf

			// It's unusual to modify a prop directly. Consider managing this aspect differently if possible.
			// For now, keeping the logic as close to original as possible.
			const originalDeviceBroadcast = device.broadcast
			device.broadcast = true

			const r = await API.sendPacket({
				deviceId: device.id,
				command: p.command,
				payloadHex: p.toRaw().toString('hex'),
				responseCommand: RFCommand.ACK
			})

			device.broadcast = originalDeviceBroadcast // Restore original value

			if (r) {
				setBusy(false)
			} else {
				setBusy(false)
				setError('Operation Failed!')
			}
		} catch (e) {
			setError('Invalid JSON format for street configuration.')
			setBusy(false)
		}
	}

	return (
		<DeviceDetailsBlock title="Street Configuration">
			<TextArea style={{ height: '110px' }} disabled={busy} value={value} onChange={(event) => setValue(event.target.value)} />
			{error && (
				<Callout icon="error" intent="danger">
					{error}
				</Callout>
			)}
			<div className="buttons">
				<Button fill text="Read" intent="success" disabled={busy} onClick={readStreetConfig} />
				<Button fill text="Write" intent="danger" disabled={busy} onClick={writeStreetConfig} />
			</div>
		</DeviceDetailsBlock>
	)
}
